import type { ReplayRowValue } from '../backend.d'
import { FaSolidAlignCenter, FaSolidBackward, FaSolidBackwardStep, FaSolidForward, FaSolidForwardStep, FaSolidPause, FaSolidPlay } from 'solid-icons/fa'
import { Component, createEffect, createMemo, createSignal, on, onMount, Show } from 'solid-js'
import { createMutable, modifyMutable, produce } from 'solid-js/store'
import toast from 'solid-toast'
import bindEngineToComponent from '../lib/canvas/bindEngineToComponentV1'
import createEngineForReplay from '../lib/core/engine/createEngineForReplay'
import type { Engine } from '../lib/core/engine/Engine.type'
import { SelectedToolId } from '../lib/core/map_editor/SelectedToolId.enum'
import { ActionType } from '../lib/core/state/flux/action/ActionType'
import dispatchReplay from '../lib/core/state/flux/dispatchReplay'
import type { State } from '../lib/core/state/State.type'
import { StateType } from '../lib/core/state/state_type.enum'
import { max } from '../lib/core/util/math/Math'
import { deepClone } from '../lib/deep_clone'
import { useSelectedToolSignal } from '../rx/signal/useSelectedToolSignal'
import { useActionLogsSignal } from '../rx/signal/useActionLogsSignal'
import { useEnginePrevStateSignal } from '../rx/signal/useEnginePrevStateSignal'
import type { ActionLog } from '../lib/core/state/flux/ActionLog.type'
import Unexpected from '../lib/Exception/Unexpected.class'
import registerContextMenuEvent from '../lib/dom/event/registerContextMenuEvent'
import registerTouchEventsV2 from '../lib/dom/event/registerTouchEventsV2'
import useCornerWidgetEngine from '../rx/shared/CornerWidget/useCornerWidgetEngine'
import createRedirectIfWrongStateTypeEffect from '../rx/effect/createRedirectIfWrongStateTypeEffect'
import { setEngineToast } from '../lib/three/fn/getSetEngineToast'
import { setEngineStateFromExternal } from '../lib/core/engine/setEngineState'
import EngineContext from './EngineContext'
import GameEndedModal from './GameEndedModal'
import { Button } from 'solid-bootstrap'

interface Props {
  row: ReplayRowValue,
}

const componentStateType = StateType.Replay

/**
 * @deprecated
 */
const ReplayPlayCanvas: Component<Props> = (props) => {
  const engine: Engine = createMutable<Engine>(createEngineForReplay())
  const [, setSelectedTool] = useSelectedToolSignal(engine)
  const [getActionLogs, setActionLogs] = useActionLogsSignal(engine)
  const [, setPrevState] = useEnginePrevStateSignal(engine)

  const [isPlaying, setIsPlaying] = createSignal(false)

  const [actionLogIndexCursor, setActionLogIndexCursor] = createSignal<number>(0)
  const [timeBetweenActionDispatches, setTimeBetweenActionDispatches] = createSignal<number>(500)

  setTimeBetweenActionDispatches(500)

  const getRawActionLogs = () => (props.row.action_logs as Array<ActionLog>)

  const getStartNewGameLogIndex = createMemo(() => getRawActionLogs().findIndex((actionLog) => actionLog.action.type === ActionType.Lobby.StartNewGame))

  // game logs without lobby logs
  const actionLogs = createMemo(() => getRawActionLogs().slice(getStartNewGameLogIndex()))

  const isAtBegining = createMemo(() => actionLogIndexCursor() === 0)
  const isAtEnd = createMemo(() => actionLogIndexCursor() > 0 && actionLogs().length === (actionLogIndexCursor() + 1))

  let lastAction: Promise<void> | null = null

  const stateCache = new Map<number, State>()

  createEffect(on(isPlaying, (isPlayingIsTrue: boolean) => {
    if (isPlayingIsTrue && !lastAction) {
      const logs = actionLogs()
      lastAction = (async () => {
        try {
          do {
            const index = actionLogIndexCursor() + 1
            const nextActionLog = logs[index]

            if (nextActionLog) {
              setActionLogIndexCursor(index)

              await dispatchReplay(engine, nextActionLog.action, nextActionLog.cref)

              stateCache.set(index, deepClone(engine.state))

              const delay = timeBetweenActionDispatches()
              if (delay > 1) {
                await new Promise((resolve) => setTimeout(() => requestAnimationFrame(resolve), delay))
              } else {
                throw new Unexpected('!>1')
              }
            } else {
              setIsPlaying(false)
            }

          } while (isPlaying())
        } finally {
          lastAction = null
        }
      })()
    } else {
      setIsPlaying(false)
    }
  }))

  async function rewindToPreviousActionLog() {
    setIsPlaying(false)

    const prevIndex = actionLogIndexCursor() - 1
    const logs = actionLogs()
    const prevActionLog = logs[prevIndex]
    if (prevActionLog) {
      const prevState = stateCache.get(prevIndex)
      if (prevState) {
        modifyMutable(engine, produce(() => {
          setEngineStateFromExternal(engine, prevState)
        }))
        setPrevState(prevState)
        setActionLogs(getActionLogs().slice(0, prevIndex + 1))
        setActionLogIndexCursor(prevIndex)
      } else {
        let cursorIndex = prevIndex - 1
        let prevState = null
        do {
          prevState = stateCache.get(cursorIndex)
          cursorIndex -= 1
        } while (!prevState && cursorIndex >= 0)

        if (!prevState) {
          prevState = logs[0].state0
        }
        if (!prevState) {
          throw new Unexpected('!prevState')
        }

        const clonedPrevState = deepClone(prevState)
        modifyMutable(engine, produce(() => {
          setEngineStateFromExternal(engine, clonedPrevState)
        }))
        setPrevState(clonedPrevState)
        setActionLogs(logs.slice(0, 1))

        do {
          cursorIndex++
          const nextActionLog = logs[cursorIndex]

          if (nextActionLog) {
            setActionLogIndexCursor(cursorIndex)
            await dispatchReplay(engine, nextActionLog.action, nextActionLog.cref)
            stateCache.set(cursorIndex, deepClone(engine.state))
          } else {
            setIsPlaying(false)
          }
        } while (prevIndex >= cursorIndex)
      }
    } else {
      throw new Unexpected('!prevActionLog')
    }
  }

  async function fastForwardToNextActionLog() {
    setIsPlaying(false)

    const nextIndex = actionLogIndexCursor() + 1
    const nextActionLog = actionLogs()[nextIndex]
    if (nextActionLog) {
      await dispatchReplay(engine, nextActionLog.action, nextActionLog.cref)
      setActionLogIndexCursor(nextIndex)
    } else {
      throw new Unexpected('!nextActionLog')
    }
  }

  function rewindToTheBeginning() {
    setIsPlaying(false)

    const logs = actionLogs()

    const index = 0
    const startNewGameLog = logs[index]
    const initialState = startNewGameLog?.state0
    setActionLogIndexCursor(index)

    if (!initialState) {
      const error = new Error('No initial state found in action logs')
      console.error(error)
      return
    }

    const clonedInitialState = deepClone(initialState)
    modifyMutable(engine, produce(() => {
      setEngineStateFromExternal(engine, clonedInitialState)
    }))
    setPrevState(clonedInitialState)
    setActionLogs([startNewGameLog])
  }

  function fastfowardToTheEnd() {
    const logs = actionLogs()
    setActionLogIndexCursor(logs.length - 1)
    setIsPlaying(false)
    const clonedEndState = deepClone(props.row.data)
    modifyMutable(engine, produce(() => {
      setEngineStateFromExternal(engine, clonedEndState)
    }))
    setPrevState(clonedEndState)
    setActionLogs(logs)
  }

  onMount(rewindToTheBeginning)

  registerContextMenuEvent(engine)

  bindEngineToComponent(engine)

  useCornerWidgetEngine(engine)

  createRedirectIfWrongStateTypeEffect(componentStateType, () => engine.state.type, () => engine.state.online?.id)
  setEngineToast(engine, toast)

  setSelectedTool(SelectedToolId.Inspect)

  return <EngineContext.Provider value={engine}>
    <div id="gpc" class="replay">
      <div id="gvp" ref={(ref: HTMLElement) => {
        if (ref) {
          registerTouchEventsV2(engine, ref)
        }
      }}>
        {/* engine.canvasList doesn't exist anymore? check createEngineForReplay */}
      </div>
      <div id="cmdp">
        <div class="d-flex justify-content-between">
          <span class="btn btn-outline-secondary disabled btn-sm m-1">Round: {engine.state.round} / {props.row.data.round}</span>
          <span class="btn btn-outline-secondary disabled btn-sm m-1">
            Action: {max(0, actionLogIndexCursor() + 1)} / {actionLogs().length}
          </span>
          <Button class="btn btn-outline-secondary btn-sm m-1">
            <FaSolidAlignCenter />{' Recenter'}
          </Button>
        </div>
        <div class="d-flex justify-content-between">
          <Button class="btn btn-outline-secondary btn-sm m-1" disabled={isAtBegining()} onClick={rewindToTheBeginning}>
            <FaSolidBackward />{' Begining'}
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1" disabled={isAtBegining()} onClick={rewindToPreviousActionLog}>
            <FaSolidBackwardStep />{' Prev'}
          </Button>
          <Show when={isPlaying()}
            children={
              <Button class="btn btn-outline-secondary btn-sm m-1" onClick={() => setIsPlaying(false)}>
                <FaSolidPause />{' Pause'}
              </Button>
            }
            fallback={
              <Button class="btn btn-outline-secondary btn-sm m-1" onClick={() => setIsPlaying(true)}>
                <FaSolidPlay />{' Play'}
              </Button>
            }
          />
          <Button class="btn btn-outline-secondary btn-sm m-1" disabled={isAtEnd()} onClick={fastForwardToNextActionLog}>
            {'Next '}
            <FaSolidForward />
          </Button>
          <Button class="btn btn-outline-secondary btn-sm m-1" disabled={isAtEnd()} onClick={fastfowardToTheEnd}>
            {'End '}<FaSolidForwardStep />
          </Button>
        </div>
      </div>
    </div>
    <GameEndedModal />
  </EngineContext.Provider>
}

export default ReplayPlayCanvas
